import { useRequiredContext } from "@redotech/react-util/context";
import { AuthContext } from "@redotech/redo-merchant-app-common/auth";
import { RedoMerchantClientContext } from "@redotech/redo-merchant-app-common/client/context";
import { REDO_EFFECT_API_URL } from "@redotech/redo-merchant-app-common/config";
import {
  EffectApiClientContext,
  EffectApiClientProvider,
  type EffectApiContext,
} from "@redotech/redo-merchant-app-common/effect-api-client-provider";
import { RedoMerchantRpcClientContext } from "@redotech/redo-merchant-app-common/rpc-client";
import { useSupportHelpdeskV2Enabled } from "@redotech/redo-merchant-app-common/setting/use-support-helpdesk-v2-enabled";
import type { SupportTicketQueryScope } from "@redotech/redo-support-v2/query/support-ticket-query-keys";
import {
  createContext,
  memo,
  type ReactNode,
  useContext,
  useMemo,
  useRef,
} from "react";
import { merchantQueryClient } from "../../react-query-client";
import { createGlobalSupportTicketCollections } from "../db/collections";
import { useSupportTicketQueryScope } from "../hooks/use-support-ticket-query-scope";
import { NotificationSyncHandlers } from "../stream/notification-sync-handlers";
import { GlobalSupportTicketCollectionsContext } from "./global-support-ticket-collections-context";

const GlobalSupportTicketStoreProvider = memo(
  function GlobalSupportTicketStoreProvider({
    children,
    queryScope,
  }: {
    children: ReactNode;
    queryScope: SupportTicketQueryScope;
  }) {
    const effectApiClient = useRequiredContext(EffectApiClientContext);
    const rpcClient = useRequiredContext(RedoMerchantRpcClientContext);
    const merchantClient = useRequiredContext(RedoMerchantClientContext);

    // Keep a ref to the latest effectApi so the stable proxy always calls
    // through to the current (non-disposed) runtime.
    const effectApiRef = useRef(effectApiClient);
    effectApiRef.current = effectApiClient;

    const stableEffectApi = useMemo<EffectApiContext>(
      () => ({
        runPromise: (...args) => effectApiRef.current.runPromise(...args),
        getClient: () => effectApiRef.current.getClient(),
      }),
      // Stable proxy — intentionally created once, reads from ref at call time.

      [],
    );

    const collections = useMemo(() => {
      return createGlobalSupportTicketCollections({
        queryClient: merchantQueryClient,
        effectApi: stableEffectApi,
        rpcClient,
        merchantClient,
        queryScope,
        tagQueryScope: queryScope,
      });
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [stableEffectApi, queryScope]);

    return (
      <GlobalSupportTicketCollectionsContext.Provider value={collections}>
        <NotificationSyncHandlers />
        {children}
      </GlobalSupportTicketCollectionsContext.Provider>
    );
  },
);

interface SupportProvidersProps {
  children: ReactNode;
}

const FeatureFlagEnabledContext = createContext<boolean | undefined>(undefined);
FeatureFlagEnabledContext.displayName = "FeatureFlagEnabledContext";

export function useFeatureFlagEnabled(): boolean {
  return useRequiredContext(FeatureFlagEnabledContext);
}

const FeatureFlagEnabledProvider = memo(function FeatureFlagEnabledProvider({
  children,
}: {
  children: ReactNode;
}) {
  const { pending, enabled } = useSupportHelpdeskV2Enabled();

  if (pending) {
    return null;
  }

  return (
    <FeatureFlagEnabledContext.Provider value={enabled}>
      {children}
    </FeatureFlagEnabledContext.Provider>
  );
});

export const SupportProviders = memo(function SupportProviders({
  children,
}: SupportProvidersProps) {
  const auth = useContext(AuthContext);
  const queryScope = useSupportTicketQueryScope();

  return (
    <EffectApiClientProvider
      authToken={auth?.token}
      baseUrl={REDO_EFFECT_API_URL}
    >
      <GlobalSupportTicketStoreProvider queryScope={queryScope}>
        <FeatureFlagEnabledProvider>{children}</FeatureFlagEnabledProvider>
      </GlobalSupportTicketStoreProvider>
    </EffectApiClientProvider>
  );
});
